import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { Link, useSearchParams } from "react-router-dom";
import { Title, useNotify, usePermissions } from "react-admin";
import { callApi } from "@/dataprovider/misc-apis";
import { hasPermission } from "@/utils/permission-utils";
import { apiErrorMessage } from "../shared/api-error";
import { formatRupees } from "../shared/fee-format";
import { MADRASA_FEES_CREATE, MADRASA_FEES_VIEW } from "../shared/madrasa-permissions";
import { StudentPicker, type StudentOption } from "../shared/StudentPicker";

type Totals = { billed: number; paid: number; outstanding: number; overdue: number };
type LedgerDue = {
  id: string;
  feeHeadName: string;
  periodLabel: string;
  dueDate: string;
  concessionAmount: number;
  netAmount: number;
  paidAmount: number;
  outstanding: number;
  status: "PENDING" | "PARTIAL" | "PAID" | "CANCELLED";
  overdue: boolean;
};
type Ledger = {
  student: { id: string; name: string; admissionNo: number; itsId: string };
  asOf: string;
  totals: Totals;
  years: Array<{
    academicYearId: string;
    academicYearName: string;
    enrollments: Array<{ id: string; className: string; status: string }>;
    totals: Totals;
    dues: LedgerDue[];
    receipts: Array<{
      id: string;
      receiptNo: string;
      receiptDate: string;
      totalAmount: number;
      paymentMode: string;
      voided: boolean;
    }>;
  }>;
};

const STATUS: Record<
  LedgerDue["status"],
  { label: string; color: "default" | "warning" | "success" }
> = {
  PENDING: { label: "Unpaid", color: "default" },
  PARTIAL: { label: "Part paid", color: "warning" },
  PAID: { label: "Paid", color: "success" },
  CANCELLED: { label: "Cancelled", color: "default" },
};

function TotalsLine({ t }: { t: Totals }) {
  return (
    <Typography variant="body2" color="text.secondary">
      Billed {formatRupees(t.billed)} · paid {formatRupees(t.paid)} · outstanding{" "}
      <strong>{formatRupees(t.outstanding)}</strong>
      {t.overdue > 0 && (
        <>
          {" "}
          · <span style={{ color: "#b00020" }}>overdue {formatRupees(t.overdue)}</span>
        </>
      )}
    </Typography>
  );
}

/** One student's dues and payments across academic years. */
export default function StudentLedgerPage() {
  const { permissions } = usePermissions();
  const notify = useNotify();
  const [student, setStudent] = useState<StudentOption | null>(null);
  const [ledger, setLedger] = useState<Ledger | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const linkedStudentId = searchParams.get("studentId");

  // Arriving from a link (for example the defaulters list) opens that student straight away.
  useEffect(() => {
    if (!linkedStudentId) return;
    callApi({ location: `madrasaStudents/${linkedStudentId}`, method: "GET" })
      .then((r) => {
        const row = (r.data as { rows?: StudentOption[] }).rows?.[0];
        if (row) setStudent(row);
      })
      .catch((error: unknown) =>
        notify(apiErrorMessage(error, "Could not open that student"), { type: "error" })
      );
  }, [linkedStudentId, notify]);

  useEffect(() => {
    if (!student) {
      setLedger(null);
      return;
    }
    setLoading(true);
    callApi({
      location: "feeReports/student-ledger",
      method: "GET",
      data: { studentId: student.id },
    })
      .then((r) => setLedger(r.data as Ledger))
      .catch((error: unknown) => {
        setLedger(null);
        notify(apiErrorMessage(error, "Could not load the ledger"), { type: "error" });
      })
      .finally(() => setLoading(false));
  }, [student, notify]);

  if (!hasPermission(permissions, MADRASA_FEES_VIEW)) return null;

  return (
    <>
      <Title title="Student fee ledger" />
      <Box sx={{ p: 2, maxWidth: 1000 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Student fee ledger
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Everything a student owes and has paid, by academic year.
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: "center" }}>
          <StudentPicker value={student} onChange={setStudent} autoFocus />
          {student && hasPermission(permissions, MADRASA_FEES_CREATE) && (
            <Button component={Link} to="/feeCollect" variant="outlined">
              Collect fee
            </Button>
          )}
        </Stack>

        {loading && <CircularProgress size={28} />}
        {ledger && !loading && (
          <>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1">
                {ledger.student.name} · adm. {ledger.student.admissionNo} · ITS{" "}
                {ledger.student.itsId}
              </Typography>
              <TotalsLine t={ledger.totals} />
            </Paper>
            {ledger.years.length === 0 && (
              <Alert severity="info">This student is not enrolled in any academic year yet.</Alert>
            )}
            {ledger.years.map((y) => (
              <Paper key={y.academicYearId} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6">{y.academicYearName}</Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  {y.enrollments
                    .map((e) => `${e.className}${e.status === "WITHDRAWN" ? " (withdrawn)" : ""}`)
                    .join(" → ")}
                </Typography>
                <Box sx={{ mb: 1 }}>
                  <TotalsLine t={y.totals} />
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Fee</TableCell>
                        <TableCell>Period</TableCell>
                        <TableCell>Due date</TableCell>
                        <TableCell align="right">To pay</TableCell>
                        <TableCell align="right">Paid</TableCell>
                        <TableCell align="right">Outstanding</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {y.dues.map((d) => {
                        const chip = STATUS[d.status];
                        return (
                          <TableRow
                            key={d.id}
                            sx={d.status === "CANCELLED" ? { opacity: 0.55 } : undefined}
                          >
                            <TableCell>{d.feeHeadName}</TableCell>
                            <TableCell>{d.periodLabel}</TableCell>
                            <TableCell>{d.dueDate}</TableCell>
                            <TableCell align="right">
                              {formatRupees(d.netAmount)}
                              {d.concessionAmount > 0 && (
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {" "}
                                  (−{formatRupees(d.concessionAmount)})
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">{formatRupees(d.paidAmount)}</TableCell>
                            <TableCell align="right">
                              {d.status === "CANCELLED" ? "—" : formatRupees(d.outstanding)}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                color={chip.color}
                                label={chip.label}
                                variant={d.status === "PENDING" ? "outlined" : "filled"}
                              />
                              {d.overdue && (
                                <Chip size="small" color="error" label="Overdue" sx={{ ml: 0.5 }} />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                  Receipts
                </Typography>
                {y.receipts.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No payments yet.
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableBody>
                      {y.receipts.map((r) => (
                        <TableRow key={r.id} sx={r.voided ? { opacity: 0.6 } : undefined}>
                          <TableCell>
                            <Link to={`/feeReceipts/${r.id}/show`}>{r.receiptNo}</Link>
                          </TableCell>
                          <TableCell>{r.receiptDate}</TableCell>
                          <TableCell>{r.paymentMode.replace("_", " ").toLowerCase()}</TableCell>
                          <TableCell align="right">{formatRupees(r.totalAmount)}</TableCell>
                          <TableCell>
                            {r.voided && <Chip size="small" color="error" label="Voided" />}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Paper>
            ))}
          </>
        )}
      </Box>
    </>
  );
}
